import {
  ChakraFlex,
  PaginationV2,
  TableColumnV2,
  TableV2,
  useDimensions,
} from '@equip.health/ui';
import { useMemo, useRef } from 'react';
import { DEFAULT_PAGINATION_CONFIG } from '~/lib/constants';
import { ScriptSureSyncFailure } from '~/store/services/integration';
import ScriptSureSyncTableRow, {
  ScriptSureSyncFailureRow,
} from './ScriptSureSyncTableRow';
import { SCRIPTSURE_SYNC_CONSTANTS } from './scriptSureSync.constants';

const { COLUMN_HEADERS, TABLE_NAME } = SCRIPTSURE_SYNC_CONSTANTS;

interface ScriptSureSyncTableProps {
  id: string;
  failures: ScriptSureSyncFailure[];
  isLoading: boolean;
  pageNumber: number;
  resyncingPatientIds: Set<string>;
  totalCount: number;
  handlePageChange: (page: number) => void;
  handleItemPerPageChange: (itemsPerPage: number) => void;
  onResync: (failure: ScriptSureSyncFailure) => void;
}

const columns: TableColumnV2<ScriptSureSyncFailureRow>[] = [
  { header: COLUMN_HEADERS.PATIENT_NAME, key: 'patientName', width: '200px' },
  { header: COLUMN_HEADERS.EMAIL, key: 'email', width: '240px' },
  { header: COLUMN_HEADERS.FAILURE_REASON, key: 'failureReason' },
  { header: COLUMN_HEADERS.FAILURE_TIME, key: 'timestamp', width: '160px' },
  { header: '', key: 'action', width: '80px' },
];

const TABLE_BOTTOM_PADDING = 50;
const PAGINATION_RIGHT_MARGIN = '30px';

const ScriptSureSyncTable = ({
  id,
  failures,
  isLoading,
  pageNumber,
  resyncingPatientIds,
  totalCount,
  handlePageChange,
  handleItemPerPageChange,
  onResync,
}: ScriptSureSyncTableProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const tableYPosition = useDimensions(tableRef, true)?.borderBox.top ?? 0;
  const windowHeight = window?.innerHeight ?? 0;
  const tableHeight =
    windowHeight -
    tableYPosition -
    DEFAULT_PAGINATION_CONFIG.paginationHeight -
    TABLE_BOTTOM_PADDING;

  const rows = useMemo(
    () =>
      failures.map((failure) =>
        ScriptSureSyncTableRow({
          id,
          failure,
          isResyncing: resyncingPatientIds.has(failure.patientId),
          onResync,
        }),
      ),
    [failures, id, onResync, resyncingPatientIds],
  );

  return (
    <ChakraFlex direction="column" gap="20px">
      <ChakraFlex
        direction="column"
        height={tableHeight}
        overflowY="auto"
        ref={tableRef}
      >
        <TableV2 columns={columns} isLoading={isLoading} rows={rows} />
      </ChakraFlex>
      <ChakraFlex marginRight={PAGINATION_RIGHT_MARGIN}>
        <PaginationV2
          currentPage={pageNumber}
          itemsPerPageOptions={DEFAULT_PAGINATION_CONFIG.pageSizeOptions}
          onItemsPerPageChange={handleItemPerPageChange}
          onPageChange={handlePageChange}
          tableName={TABLE_NAME}
          totalItems={totalCount}
        />
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default ScriptSureSyncTable;
